/**
 * React Query hook for Office Locations Dropdown with Pagination Support
 * Uses getOfficeLocationsV2 API with is_dropdown=true and include_contacts=true
 * Designed for use with react-select-async-paginate
 * Provides automatic caching, background sync, and proper pagination handling
 */

import { useQuery } from '@tanstack/react-query';
import { getOfficeLocationsV2 } from 'services/Settings';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches office locations for dropdown with pagination
 *
 * @param {Object} options - Query options
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {number} options.page - Current page number
 * @param {string} options.searchQuery - Search query for filtering
 * @returns {Promise<Object>} - { options, hasMore, additional: { page } }
 */
const fetchOfficeLocationsPage = async ({ pageSize = 20, page = 1, searchQuery = '' }) => {
  try {
    const response = await getOfficeLocationsV2({
      keyword: searchQuery,
      page,
      limit: pageSize,
      sort_by: 'office_name',
      sort_order: 'asc',
      is_dropdown: true,
      include_contacts: true,
    });

    const data = response?.data || {};
    const locations = data.data || [];

    // Transform to react-select format
    const options = locations.map((location) => ({
      label: location.office_name,
      value: location.office_location_id,
      ...location, // Include full object for additional data access
    }));

    return {
      options,
      hasMore: data.has_next || false,
      additional: {
        page: data.page || 1,
        totalCount: data.total_count || 0,
      },
    };
  } catch (error) {
    console.error('Error fetching office locations page:', error);
    throw error;
  }
};

/**
 * Hook to fetch paginated office locations for dropdown
 * Compatible with react-select-async-paginate
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query for filtering
 * @param {number} params.page - Current page number (default: 1)
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {boolean} params.enabled - Enable/disable query (default: true)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useOfficeLocationsDropdown = ({ searchQuery = '', page = 1, pageSize = 20, enabled = true } = {}) => {
  const queryKey = queryKeys.officeLocations?.dropdown
    ? queryKeys.officeLocations.dropdown({
        searchQuery,
        page,
        pageSize,
      })
    : ['officeLocations', 'dropdown', { searchQuery, page, pageSize }];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchOfficeLocationsPage({
        pageSize,
        page,
        searchQuery,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime in v4)
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    data: data || { options: [], hasMore: false, additional: { page: 1, totalCount: 0 } },
    isLoading,
    error,
    refetch,
  };
};

/**
 * Load options function for react-select-async-paginate
 * This is a standalone function that can be used directly with AsyncPaginate
 *
 * @param {string} search - Search query
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - Additional data (current page)
 * @param {Object} config - Configuration options
 * @returns {Promise<Object>} - { options, hasMore, additional }
 */
export const loadOfficeLocationOptions = async (search, loadedOptions, additional, config = {}) => {
  const { pageSize = 20 } = config;

  try {
    // Handle initial load (page is undefined or 0) vs subsequent loads
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const pageToFetch = currentPage + 1; // First load: 0 + 1 = 1, Second load: 1 + 1 = 2, etc.

    const result = await fetchOfficeLocationsPage({
      pageSize,
      page: pageToFetch,
      searchQuery: search,
    });

    return {
      ...result,
      additional: {
        page: pageToFetch,
        totalCount: result.additional.totalCount,
      },
    };
  } catch (error) {
    console.error('Error loading office location options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { page: 0, totalCount: 0 },
    };
  }
};
